import * as XLSX from 'xlsx';
import type { LegacyV0UserMigrationExportPayload } from '../../services/legacyV0UserManager';
import { formatV0Date, isAbbonamentoActive, todayYyyymmdd } from './v0DateUtils';

const str = (value: unknown): string => {
  if (value == null) return '';
  return String(value);
};

export type V0ContactExcelRow = Record<string, string | number | boolean>;

const EMPTY_HEADERS = [
  'post_id',
  'email',
  'in_be2025',
  'email_is_fallback',
  'nome',
  'cognome',
  'titolo',
  'ragione_sociale',
  'professione',
  'tipo_cliente',
  'citta',
  'provincia',
  'stato',
  'lingua',
  'data_iscrizione',
  'accettazione_privacy',
  'data_accettazione_eula',
  'devices_count',
  'devices',
  'abbonamenti_attivi_count',
  'abbonamenti_attivi',
] as const;

/** Flatten one v0 export into a single contact row (user + devices + active abbonamenti). */
export const v0ExportToContactRow = (
  payload: LegacyV0UserMigrationExportPayload,
  today = todayYyyymmdd(),
  inBe2025 = false
): V0ContactExcelRow => {
  const source = (payload.source ?? {}) as Record<string, unknown>;
  const profile = (source.profile ?? {}) as Record<string, unknown>;
  const wpUser = (source.wp_user ?? null) as Record<string, unknown> | null;
  const oculus = (Array.isArray(source.oculus) ? source.oculus : []) as Array<
    Record<string, unknown>
  >;
  const abbonamenti = (
    Array.isArray(source.abbonamenti) ? source.abbonamenti : []
  ) as Array<Record<string, unknown>>;

  const activeAbbonamenti = abbonamenti.filter((row) =>
    isAbbonamentoActive(row.data_fine, today)
  );

  const devices = oculus
    .map((row) => {
      const id = str(row.oculus_id);
      const user = str(row.oculus_user);
      if (!id && !user) return '';
      return user ? `${id} (${user})` : id;
    })
    .filter(Boolean)
    .join('; ');

  const abbonamentiAttivi = activeAbbonamenti
    .map((row) => {
      const remap = (row.product_remap ?? {}) as Record<string, unknown>;
      const product =
        str(remap.be_product_name) || str(row.categoria_titolo) || '—';
      const fine = formatV0Date(row.data_fine);
      return `${product} → ${fine}`;
    })
    .join('; ');

  return {
    post_id: payload.legacy_user_id,
    email: payload.email ?? '',
    in_be2025: inBe2025 ? 'yes' : 'no',
    email_is_fallback: Boolean(payload.email_is_fallback),
    nome: str(profile.nome),
    cognome: str(profile.cognome),
    titolo: str(profile.titolo),
    ragione_sociale: str(profile.ragione_sociale),
    professione: str(profile.professione),
    tipo_cliente: str(profile.tipo_cliente),
    citta: str(profile.citta),
    provincia: str(profile.provincia),
    stato: str(profile.stato),
    lingua: str(profile.lingua),
    data_iscrizione: str(wpUser?.user_registered),
    accettazione_privacy: str(profile.accettazione_privacy),
    data_accettazione_eula: str(profile.data_accettazione_eula),
    devices_count: oculus.length,
    devices,
    abbonamenti_attivi_count: activeAbbonamenti.length,
    abbonamenti_attivi: abbonamentiAttivi,
  };
};

/** One workbook, one sheet, one row per contact. */
export const buildV0ContactsExcelWorkbook = (
  payloads: LegacyV0UserMigrationExportPayload[],
  presentEmails: Set<string> = new Set()
): XLSX.WorkBook => {
  const today = todayYyyymmdd();
  const rows = payloads.map((payload) => {
    const email = (payload.email || '').trim().toLowerCase();
    const inBe2025 = Boolean(email && presentEmails.has(email));
    return v0ExportToContactRow(payload, today, inBe2025);
  });
  const workbook = XLSX.utils.book_new();
  const sheet =
    rows.length > 0
      ? XLSX.utils.json_to_sheet(rows)
      : XLSX.utils.aoa_to_sheet([EMPTY_HEADERS as unknown as string[]]);
  XLSX.utils.book_append_sheet(workbook, sheet, 'Contatti');

  return workbook;
};

export const v0ContactsExcelFilename = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `legacy-v0-contacts-${y}${m}${day}.xlsx`;
};
